import React, { useState, useEffect } from 'react';
import { motion } from "framer-motion";
import { Check, X, Star, Users, Shield, DollarSign, Zap, BarChart3, Globe2, Award, ArrowRight, Play, TrendingUp, Clock, Target, Menu, ChevronRight, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import CompanyLayout from "@/components/CompanyLayout";
import { DemoBookingModal } from "@/components/DemoBookingModal";
import { SupademoModal } from "@/components/SupademoModal";

const AssemblyVsBonusly = () => {
  const [activeTab, setActiveTab] = useState('recognition');
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const [isSupademoModalOpen, setIsSupademoModalOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('overview');

  // Table of Contents items
  const tocItems = [
    { id: 'overview', label: 'Overview' },
    { id: 'features', label: 'Features' },
    { id: 'pricing', label: 'Pricing' },
    { id: 'ease-of-use', label: 'Ease of Use' },
    { id: 'reviews', label: 'Reviews' },
    { id: 'roi', label: 'ROI' },
    { id: 'comparison', label: 'Which Platform' },
    { id: 'conclusion', label: 'Conclusion' },
    { id: 'faqs', label: 'FAQs' }
  ];

  // Scroll tracking for active section
  useEffect(() => {
    const handleScroll = () => {
      const sections = tocItems.map(item => document.getElementById(item.id));
      const scrollPosition = window.scrollY + 100;

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(tocItems[i].id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setIsMobileMenuOpen(false);
  };

  const featureCategories = {
    features: "Core Features",
    analytics: "Analytics & Insights", 
    support: "Support & Security"
  };

  const comparisonData = {
    features: [
      { feature: "AI-Powered Recognition", assembly: true, bonusly: false, highlight: true },
      { feature: "Peer-to-Peer Recognition", assembly: true, bonusly: true },
      { feature: "Manager 1:1 Tools", assembly: true, bonusly: false, highlight: true },
      { feature: "Employee Surveys", assembly: true, bonusly: false, highlight: true },
      { feature: "Custom Branding", assembly: true, bonusly: true },
      { feature: "Slack/Teams Integration", assembly: true, bonusly: true },
    ],
    analytics: [
      { feature: "Advanced Analytics Dashboard", assembly: true, bonusly: false, highlight: true },
      { feature: "AI-Driven Insights", assembly: true, bonusly: false, highlight: true },
      { feature: "Recognition Gap Analysis", assembly: true, bonusly: false, highlight: true },
      { feature: "Performance Correlation", assembly: true, bonusly: false, highlight: true },
      { feature: "Basic Participation Tracking", assembly: true, bonusly: true },
      { feature: "Export Capabilities", assembly: true, bonusly: true },
    ],
    support: [
      { feature: "24/7 Customer Support", assembly: true, bonusly: false, highlight: true },
      { feature: "Unlimited Training", assembly: true, bonusly: false, highlight: true },
      { feature: "Fast Deployment (Hours)", assembly: true, bonusly: false, highlight: true },
      { feature: "GDPR Compliant", assembly: true, bonusly: false, highlight: true },
      { feature: "SOC 2 Type II", assembly: true, bonusly: true },
      { feature: "API Access", assembly: true, bonusly: true },
    ]
  };

  const pricingData = [
    { feature: "Free Plan Available", assembly: true, bonusly: false, highlight: true },
    { feature: "No Transaction Fees", assembly: true, bonusly: false, highlight: true },
    { feature: "Points Never Expire", assembly: true, bonusly: false, highlight: true },
    { feature: "Transparent Pricing", assembly: true, bonusly: false, highlight: true },
    { feature: "Global Rewards", assembly: true, bonusly: true },
    { feature: "Budget Controls", assembly: true, bonusly: true },
  ];

  return (
    <CompanyLayout
      title="Assembly vs Bonusly: Complete Comparison Guide | Employee Recognition Software"
      description="Compare Assembly and Bonusly employee recognition platforms. See detailed analysis of features, pricing, G2 reviews, and why Assembly ranks #1 for easiest to use. Make the right choice for your team."
      ogImage="/assembly-vs-bonusly-hero.png"
      canonicalUrl="https://assembly.com/compare/assembly-vs-bonusly"
      keywords="Assembly vs Bonusly, employee recognition software comparison, Assembly reviews, Bonusly reviews, G2 rankings, employee engagement platform, recognition platform features, employee rewards software"
      author="Assembly Content Team"
    >
      {/* Hero Section - Full Width */}
      <section className="relative pt-20 pb-12 bg-gradient-to-br from-background via-primary/5 to-secondary/10 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-tr from-primary/10 via-transparent to-secondary/5"></div>
          <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-gradient-to-br from-primary/15 to-transparent rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/3 right-1/4 w-80 h-80 bg-gradient-to-bl from-secondary/12 to-transparent rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 pt-20 pb-8">
          {/* H1 Header - Full Width Above Everything */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="hidden lg:block max-w-7xl mx-auto mb-12"
          >
            <h1 className="w-full text-4xl xl:text-5xl font-bold leading-tight bg-gradient-to-r from-foreground via-foreground to-foreground/80 bg-clip-text">
              Assembly vs Bonusly
            </h1>
          </motion.div>

          {/* Desktop Layout: Text Left, Image Right */}
          <div className="hidden lg:grid lg:grid-cols-2 gap-16 items-center max-w-7xl mx-auto">
            {/* Left Side - Text Stack */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-8"
            >
              <div className="space-y-6">
                <h2 className="text-2xl xl:text-3xl font-medium text-muted-foreground leading-relaxed">
                  Side-by-Side Comparison Backed by Real User Reviews
                </h2>
                
                <p className="text-lg text-muted-foreground leading-relaxed max-w-xl">
                  Choosing the right employee recognition software is about real results, not just features. Research shows <a href="https://www.quantumworkplace.com/future-of-work/importance-of-employee-recognition" target="_blank" rel="nofollow noopener" className="text-foreground/70 hover:text-foreground hover:underline">recognition programs</a> reduce turnover by 31% and make companies 12× more likely to succeed.
                </p>
              </div>
              
              <div>
                <Button 
                  size="lg" 
                  className="px-8 py-4 text-lg font-semibold bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg hover:shadow-xl transition-all duration-200"
                  onClick={() => setIsSupademoModalOpen(true)}
                >
                  See Assembly in Action
                </Button>
              </div>
            </motion.div>
            
            {/* Right Side - Current Image Block */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative group"
            >
              <div className="relative">
                {/* Optional glow effect on hover */}
                <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-secondary/20 rounded-lg blur-xl transform rotate-1 group-hover:from-primary/30 group-hover:to-secondary/30 transition-all duration-300"></div>
                <img 
                  src="/assembly-vs-bonusly-hero.png" 
                  alt="Assembly vs Bonusly comparison - Assembly card vs Bonusly card with lightning badge showing Assembly's advantages"
                  className="relative w-full h-auto rounded-xl shadow-2xl group-hover:shadow-3xl transition-shadow duration-300"
                />
              </div>
            </motion.div>
          </div>
          
          {/* Mobile Layout: H1 → Subheading → Image → Intro → CTA → Trust Badge */}
          <div className="lg:hidden space-y-8 max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center space-y-6"
            >
              <h2 className="text-3xl sm:text-4xl font-bold leading-tight bg-gradient-to-r from-foreground via-foreground to-foreground/80 bg-clip-text">
                Assembly vs Bonusly
              </h2>
              
              <h2 className="text-xl sm:text-2xl font-medium text-muted-foreground leading-relaxed px-4">
                Side-by-Side Comparison Backed by Real User Reviews
              </h2>
            </motion.div>
            
            {/* Current Image Block */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-secondary/20 rounded-lg blur-xl transform rotate-1"></div>
                <img 
                  src="/assembly-vs-bonusly-hero.png" 
                  alt="Assembly vs Bonusly comparison - Assembly card vs Bonusly card with lightning badge showing Assembly's advantages"
                  className="relative w-full h-auto rounded-xl shadow-2xl"
                />
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-center space-y-6 px-4"
            >
              <p className="text-base text-muted-foreground leading-relaxed">
                Choosing the right employee recognition software is about real results, not just features. Research shows <a href="https://www.quantumworkplace.com/future-of-work/importance-of-employee-recognition" target="_blank" rel="nofollow noopener" className="text-foreground/70 hover:text-foreground hover:underline">recognition programs</a> reduce turnover by 31% and make companies 12× more likely to succeed.
              </p>
              
              <Button 
                size="lg" 
                className="w-full sm:w-auto px-8 py-4 text-lg font-semibold bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg hover:shadow-xl transition-all duration-200"
                onClick={() => setIsSupademoModalOpen(true)}
              >
                See Assembly in Action
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Content Layout with Sidebar */}
      <div className="flex min-h-screen bg-background">
        {/* Desktop Sidebar */}
        <aside className="hidden lg:block w-80 p-6">
          <div className="sticky top-20 space-y-6">
            {/* Table of Contents - Containerized */}
            <Card className="bg-card/50 backdrop-blur-sm border border-border/50 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-1 h-6 bg-gradient-to-b from-primary to-primary/50 rounded-full"></div>
                  <h3 className="text-lg font-semibold text-foreground">Contents</h3>
                </div>
                <nav className="space-y-1">
                  {tocItems.map((item, index) => (
                    <button
                      key={item.id}
                      onClick={() => scrollToSection(item.id)}
                      className={`group flex items-center w-full text-left px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                        activeSection === item.id
                          ? 'bg-primary/10 text-primary font-medium border border-primary/20'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                      }`}
                    >
                      <span className={`flex items-center justify-center w-5 h-5 rounded-md mr-3 text-xs font-medium transition-colors ${
                        activeSection === item.id 
                          ? 'bg-primary/20 text-primary' 
                          : 'bg-muted/40 text-muted-foreground group-hover:bg-muted group-hover:text-foreground'
                      }`}>
                        {index + 1}
                      </span>
                      <span>{item.label}</span>
                      {activeSection === item.id && (
                        <ChevronRight className="h-3 w-3 ml-auto text-primary" />
                      )}
                    </button>
                  ))}
                </nav>
              </CardContent>
            </Card>

            {/* Assembly Widget - Modernized */}
            <Card className="bg-gradient-to-br from-primary/5 via-primary/3 to-transparent border border-primary/20 shadow-xl">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 flex-shrink-0">
                    <img 
                      src="/assets/assembly-logo-small.png" 
                      alt="Assembly Logo"
                      className="w-12 h-12 object-contain"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1 mb-1">
                      <div className="flex">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="h-3 w-3 text-yellow-400 fill-current" />
                        ))}
                      </div>
                      <span className="text-xs font-medium text-primary ml-1">4.9/5</span>
                    </div>
                    <p className="text-xs text-muted-foreground">Top-rated platform</p>
                  </div>
                </div>
                
                <div className="space-y-2 mb-4">
                  {[
                    { text: "Free Forever Plan", icon: Check },
                    { text: "24/7 Support", icon: Check }
                  ].map((item, index) => (
                    <div key={index} className="flex items-center gap-2 text-xs">
                      <item.icon className="h-3 w-3 text-green-600 flex-shrink-0" />
                      <span className="text-muted-foreground">{item.text}</span>
                    </div>
                  ))}
                </div>

                <Button 
                  className="w-full h-9 text-sm font-medium bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 shadow-md hover:shadow-lg transition-all duration-200"
                  asChild
                >
                  <a href="/pricing">
                    Start Free
                  </a>
                </Button>
              </CardContent>
            </Card>
          </div>
        </aside>

        {/* Mobile TOC Toggle */}
        <div className="lg:hidden fixed top-4 left-4 z-40">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="bg-background/80 backdrop-blur-sm"
          >
            <Menu className="h-4 w-4" />
          </Button>
        </div>

        {/* Mobile TOC Overlay */}
        {isMobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 z-50 bg-background/80 backdrop-blur-sm">
            <div className="bg-background border-r border-border w-80 h-full p-6 overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold">Contents</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              
              <nav>
                <ul className="space-y-2">
                  {tocItems.map((item) => (
                    <li key={item.id}>
                      <button
                        onClick={() => scrollToSection(item.id)}
                        className={`flex items-center w-full text-left px-3 py-2 rounded-lg transition-colors ${
                          activeSection === item.id
                            ? 'bg-primary text-primary-foreground font-medium'
                            : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                        }`}
                      >
                        <ChevronRight className="h-4 w-4 mr-2" />
                        {item.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>
          </div>
        )}

        {/* Main Content */}
        <main className="flex-1 w-full bg-gradient-to-br from-background via-background/98 to-background/95">
          {/* Content Sections */}
          <div className="container mx-auto px-6 sm:px-8 lg:px-12 lg:pl-8">
            {/* Overview Section */}
            <section id="overview" className="py-20">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-4xl mb-8 text-left">1. Platform Comparison at a Glance</h2>
                
                <div className="max-w-6xl xl:max-w-7xl 2xl:max-w-full mx-auto mb-12">
                  <Card className="overflow-hidden shadow-xl border-0">
                    {/* Desktop Header */}
                    <div className="hidden md:block bg-gradient-to-r from-primary/5 to-secondary/5 p-6">
                      <div className="grid grid-cols-[200px_1fr_1fr] xl:grid-cols-[280px_1fr_1fr] gap-4 xl:gap-6 font-semibold text-lg">
                        <div className="text-left">Feature</div>
                        <div className="text-center">Assembly</div>
                        <div className="text-center">Bonusly</div>
                      </div>
                    </div>
                    
                    {/* Mobile Header */}
                    <div className="md:hidden bg-gradient-to-r from-primary/5 to-secondary/5 p-4">
                      <h3 className="font-semibold text-lg text-center">Platform Comparison</h3>
                    </div>
                    
                    <div className="divide-y divide-border">
                      {[
                        {
                          feature: "Best For",
                          assembly: "Companies that want recognition, rewards, and culture tools in one platform, built to scale",
                          bonusly: "Teams that want simple peer-to-peer recognition with basic rewards",
                          highlight: true
                        },
                        {
                          feature: "Recognition",
                          assembly: "Peer-to-peer, top-down, automated workflows, milestones, and custom programs",
                          bonusly: "Peer-to-peer recognition with points and gift cards",
                          highlight: false
                        },
                        {
                          feature: "Rewards",
                          assembly: "Global catalog, gift cards, company swag, custom rewards, and integrations with vendors",
                          bonusly: "Gift cards and digital rewards catalog",
                          highlight: true
                        },
                        {
                          feature: "Integrations",
                          assembly: "Slack, Microsoft Teams, HRIS (ADP, BambooHR, Rippling, more)",
                          bonusly: "Slack, Microsoft Teams, limited HRIS",
                          highlight: false
                        },
                        {
                          feature: "Analytics",
                          assembly: "Advanced dashboards, ROI reporting, manager insights",
                          bonusly: "Basic activity reports",
                          highlight: true
                        },
                        {
                          feature: "Pricing",
                          assembly: "Flexible plans, transparent pricing, free-to-start options",
                          bonusly: "Per-user monthly subscription, less flexible",
                          highlight: true
                        },
                        {
                          feature: "User Feedback (G2)",
                          assembly: "Highly rated for ease of use, customer support, and breadth of features",
                          bonusly: "Well-rated for recognition, but feedback notes admin limitations",
                          highlight: false
                        },
                        {
                          feature: "Overall Value",
                          assembly: "Built to grow with your organization and deliver measurable ROI",
                          bonusly: "Simple, popular with smaller teams, less customizable",
                          highlight: true
                        }
                      ].map((row, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0 }}
                          whileInView={{ opacity: 1 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.3, delay: index * 0.05 }}
                          className={`hover:bg-muted/50 transition-colors ${
                            row.highlight ? 'bg-primary/5' : ''
                          }`}
                        >
                          {/* Desktop Layout */}
                          <div className="hidden md:grid md:grid-cols-[200px_1fr_1fr] xl:grid-cols-[280px_1fr_1fr] gap-4 xl:gap-6 p-6 items-start">
                            <div className="font-semibold text-foreground flex items-center">
                              {row.feature}
                            </div>
                            <div className="text-sm text-foreground leading-relaxed">
                              {row.assembly}
                            </div>
                            <div className="text-sm text-muted-foreground leading-relaxed">
                              {row.bonusly}
                            </div>
                          </div>
                          
                          {/* Mobile Layout */}
                          <div className="md:hidden p-4 space-y-4">
                            <div className="font-semibold text-foreground text-base mb-3">
                              {row.feature}
                            </div>
                            <div className="space-y-3">
                              <div className="bg-primary/10 p-3 rounded-lg">
                                <div className="font-medium text-primary text-sm mb-1">Assembly</div>
                                <div className="text-sm text-foreground leading-relaxed">
                                  {row.assembly}
                                </div>
                              </div>
                              <div className="bg-muted/50 p-3 rounded-lg">
                                <div className="font-medium text-muted-foreground text-sm mb-1">Bonusly</div>
                                <div className="text-sm text-muted-foreground leading-relaxed">
                                  {row.bonusly}
                                </div>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </Card>
                </div>
              </motion.div>
            </section>

            {/* Features Comparison Section */}
            <section id="features" className="py-20 bg-muted/30 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="text-left mb-12"
              >
                <h2 className="text-4xl font-bold mb-4">2. Feature Comparison</h2>
                <p className="text-base text-muted-foreground text-left mx-auto">
                  Choosing between the platforms comes down to the features your team values most. Understanding <a href="https://www.shrm.org/topics-tools/flagships/all-things-work/employee-recognition-can-go-long-way-but-has-to-be-done-right" target="_blank" rel="nofollow noopener" className="text-foreground/70 hover:text-foreground hover:underline">why recognition matters</a> helps inform the right choice. Here's a quick side-by-side look at how the two platforms compare across recognition, rewards, analytics, and support.
                </p>
              </motion.div>

              {/* Category Tabs */}
              <div className="flex flex-wrap justify-center gap-2 mb-12">
                {Object.entries({
                  recognition: "Recognition & Engagement",
                  rewards: "Rewards & Gifting", 
                  analytics: "Analytics & Tools",
                  security: "Security & Compliance",
                  support: "Support & Satisfaction"
                }).map(([key, label]) => (
                  <button
                    key={key}
                    onClick={() => setActiveTab(key)}
                    className={`px-6 py-3 rounded-full font-medium transition-all duration-200 ${
                      activeTab === key
                        ? 'bg-primary text-primary-foreground shadow-lg'
                        : 'bg-card text-muted-foreground hover:bg-muted border'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {/* Comparison Table */}
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="max-w-6xl xl:max-w-7xl 2xl:max-w-full mx-auto"
              >
                <Card className="overflow-hidden shadow-xl border-0">
                  {/* Desktop Header */}
                  <div className="hidden md:block bg-gradient-to-r from-primary/5 to-secondary/5 p-6">
                    <div className="grid grid-cols-[200px_1fr_1fr] xl:grid-cols-[280px_1fr_1fr] gap-4 xl:gap-6 font-semibold text-lg">
                      <div>Feature</div>
                      <div className="text-center">Assembly</div>
                      <div className="text-center">Bonusly</div>
                    </div>
                  </div>
                  
                  {/* Mobile Header */}
                  <div className="md:hidden bg-gradient-to-r from-primary/5 to-secondary/5 p-4">
                    <h3 className="font-semibold text-lg text-center">Feature Comparison</h3>
                  </div>
                  
                  <div className="divide-y divide-border">
                    {(() => {
                      const featureData = {
                        recognition: [
                          { feature: "Peer-to-Peer Recognition", assembly: "✅ Values-based, live social feed", bonusly: "✅ Points-based recognition", highlight: false },
                          { feature: "Manager-to-Employee Recognition", assembly: "✅ Structured awards & challenges", bonusly: "⚠️ Limited — mainly peer recognition", highlight: true },
                          { feature: "AI-Powered Recognition", assembly: "✅ Drafts text in seconds, leverages the power of small wins", bonusly: "❌ Not available", highlight: true },
                          { feature: "Automated Celebrations", assembly: "✅ Birthdays, anniversaries, new hires (customizable bots)", bonusly: "✅ Birthdays & work anniversaries", highlight: false },
                          { feature: "Broader Engagement Tools", assembly: "✅ Announcements and engagement tools", bonusly: "❌ Recognition-focused only", highlight: true }
                        ],
                        rewards: [
                          { feature: "Rewards Catalog", assembly: "✅ Gift cards, experiences, swag, culture-driven perks with flexible rewards", bonusly: "✅ 1,200+ gift cards in 200+ countries", highlight: false },
                          { feature: "Points Expiration", assembly: "❌ Never expire", bonusly: "⚠️ Monthly budget resets (unused points lost)", highlight: true },
                          { feature: "Transaction Fees", assembly: "❌ No hidden fees", bonusly: "⚠️ PayPal/Visa cash-out may include extra point costs", highlight: true },
                          { feature: "Flexibility", assembly: "✅ Custom & culture-first rewards", bonusly: "⚠️ Mostly digital gift cards", highlight: true },
                          { feature: "Recognition Budgets", assembly: "✅ Flexible controls, boosts for achievements", bonusly: "⚠️ Rigid reset schedules", highlight: true }
                        ],
                        analytics: [
                          { feature: "Engagement Analytics", assembly: "✅ Recognition + surveys, adoption, ROI reporting", bonusly: "⚠️ Basic recognition activity & hashtags", highlight: true },
                          { feature: "Manager Dashboards", assembly: "✅ Recognition habits, 1:1 tracking, performance context", bonusly: "✅ Manager digests with summaries", highlight: false },
                          { feature: "AI Insights", assembly: "✅ Dora Hub with predictive insights", bonusly: "❌ Not available", highlight: true }
                        ],
                        security: [
                          { feature: "SOC 2 Type II", assembly: "✅", bonusly: "✅", highlight: false },
                          { feature: "GDPR Compliance", assembly: "✅", bonusly: "❌ U.S.-only data storage focus", highlight: true },
                          { feature: "Enterprise Security Reviews", assembly: "✅ Fast turnaround, dedicated support", bonusly: "⚠️ Available, but less emphasized", highlight: true }
                        ],
                        support: [
                          { feature: "G2 Rating", assembly: "⭐ 4.9/5 (2,400+ reviews)", bonusly: "⭐ 4.7/5 (5,200+ reviews)", highlight: true },
                          { feature: "Awards", assembly: "G2 Leader, Capterra Shortlist, Best Software 2025", bonusly: "Well-regarded but fewer awards", highlight: true },
                          { feature: "Support", assembly: "✅ 24/7 support, unlimited training, rapid deployment", bonusly: "⚠️ Strong onboarding, but some admin tool complaints", highlight: true },
                          { feature: "Highlighted Feedback", assembly: "\"Game changer for culture & engagement\" – HR Generalist", bonusly: "\"Easy to use, good for peer recognition\" – common sentiment", highlight: false }
                        ]
                      };
                      
                      return featureData[activeTab]?.map((row, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.3, delay: index * 0.05 }}
                          className={`hover:bg-muted/50 transition-colors ${
                            row.highlight ? 'bg-primary/5' : ''
                          }`}
                        >
                          {/* Desktop Layout */}
                          <div className="hidden md:grid md:grid-cols-[200px_1fr_1fr] xl:grid-cols-[280px_1fr_1fr] gap-4 xl:gap-6 p-6 items-start">
                            <div className="font-medium flex items-center">
                              {row.feature}
                            </div>
                            <div className="text-sm leading-relaxed">
                              {row.assembly}
                            </div>
                            <div className="text-sm text-muted-foreground leading-relaxed">
                              {row.bonusly}
                            </div>
                          </div>
                          
                          {/* Mobile Layout */}
                          <div className="md:hidden p-4 space-y-4">
                            <div className="font-medium text-foreground text-base mb-3">
                              {row.feature}
                            </div>
                            <div className="space-y-3">
                              <div className="bg-primary/10 p-3 rounded-lg">
                                <div className="font-medium text-primary text-sm mb-1">Assembly</div>
                                <div className="text-sm text-foreground leading-relaxed">
                                  {row.assembly}
                                </div>
                              </div>
                              <div className="bg-muted/50 p-3 rounded-lg">
                                <div className="font-medium text-muted-foreground text-sm mb-1">Bonusly</div>
                                <div className="text-sm text-muted-foreground leading-relaxed">
                                  {row.bonusly}
                                </div>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      )) || [];
                    })()}
                  </div>
                </Card>
                
                {/* Closing Summary */}
                <div className="mt-12 max-w-6xl mx-auto">
                  <Card className="p-8 bg-gradient-to-r from-primary/5 to-secondary/5 border-primary/20">
                    <div className="text-left">
                      <h3 className="text-2xl font-bold mb-4 text-left">The Bottom Line</h3>
                      <p className="text-base text-muted-foreground leading-relaxed text-left">
                        Bonusly is great for simple <a href="https://www.shrm.org/topics-tools/news/technology/put-recognition-employees-hands" target="_blank" rel="nofollow noopener" className="text-foreground/70 hover:text-foreground hover:underline">peer-to-peer recognition</a>, but Assembly offers broader tools, AI-powered insights, and stronger ROI tracking—making it the more flexible choice for growing teams.
                      </p>
                    </div>
                  </Card>
                </div>
              </motion.div>
            </section>

            {/* Pricing Section */}
            <section id="pricing" className="py-20">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="text-left mb-12"
              >
                <h2 className="text-4xl font-bold mb-4">3. Pricing Models</h2>
                <p className="text-base text-muted-foreground text-left mx-auto">
                  Transparent pricing vs hidden fees - see why Assembly delivers more value for your investment
                </p>
              </motion.div>

              <div className="max-w-6xl xl:max-w-7xl 2xl:max-w-full mx-auto">
                <Card className="overflow-hidden shadow-xl border-0">
                  {/* Desktop Header */}
                  <div className="hidden md:block bg-gradient-to-r from-primary/5 to-secondary/5 p-6">
                    <div className="grid grid-cols-[200px_1fr_1fr] xl:grid-cols-[280px_1fr_1fr] gap-4 xl:gap-6 font-semibold text-lg">
                      <div className="text-left">Plan Level</div>
                      <div className="text-center">Assembly</div>
                      <div className="text-center">Bonusly</div>
                    </div>
                  </div>
                  
                  {/* Mobile Header */}
                  <div className="md:hidden bg-gradient-to-r from-primary/5 to-secondary/5 p-4">
                    <h3 className="font-semibold text-lg text-center">Pricing Comparison</h3>
                  </div>
                  
                  <div className="divide-y divide-border">
                    {[
                      {
                        level: "Free",
                        assembly: "✅ Always Free Plan (basic recognition + gifting)",
                        bonusly: "❌ Free trial only",
                        highlight: true
                      },
                      {
                        level: "Entry",
                        assembly: "Celebrate – $2/user/mo → recognition, milestones, announcements, awards, challenges, global catalog, integrations",
                        bonusly: "Appreciate – $2/user/mo → peer recognition, gift cards, basic analytics, Slack/Teams",
                        highlight: false
                      },
                      {
                        level: "Mid",
                        assembly: "Empower – $3/user/mo → AI insights, advanced analytics, budget controls",
                        bonusly: "Connect – $5/user/mo → advanced analytics, custom rewards, HRIS integrations",
                        highlight: true
                      },
                      {
                        level: "Top",
                        assembly: "Comparable features already at lower tiers",
                        bonusly: "Achieve – $7/user/mo → workflow customization, API access",
                        highlight: true
                      }
                    ].map((row, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                        className={`hover:bg-muted/50 transition-colors ${
                          row.highlight ? 'bg-primary/5' : ''
                        }`}
                      >
                        {/* Desktop Layout */}
                        <div className="hidden md:grid md:grid-cols-[200px_1fr_1fr] xl:grid-cols-[280px_1fr_1fr] gap-4 xl:gap-6 p-6 items-start">
                          <div className="font-semibold text-foreground flex items-center">
                            {row.level}
                          </div>
                          <div className="text-sm text-foreground leading-relaxed">
                            {row.assembly}
                          </div>
                          <div className="text-sm text-muted-foreground leading-relaxed">
                            {row.bonusly}
                          </div>
                        </div>
                        
                        {/* Mobile Layout */}
                        <div className="md:hidden p-4 space-y-4">
                          <div className="font-semibold text-foreground text-base mb-3">
                            {row.level}
                          </div>
                          <div className="space-y-3">
                            <div className="bg-primary/10 p-3 rounded-lg">
                              <div className="font-medium text-primary text-sm mb-1">Assembly</div>
                              <div className="text-sm text-foreground leading-relaxed">
                                {row.assembly}
                              </div>
                            </div>
                            <div className="bg-muted/50 p-3 rounded-lg">
                              <div className="font-medium text-muted-foreground text-sm mb-1">Bonusly</div>
                              <div className="text-sm text-muted-foreground leading-relaxed">
                                {row.bonusly}
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </Card>

                <div className="mt-8 p-6 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-start">
                    <DollarSign className="h-6 w-6 text-yellow-600 mr-3 mt-1" />
                    <div>
                      <div className="font-semibold text-yellow-800 mb-2">Hidden Cost Alert</div>
                      <p className="text-yellow-700">
                        Bonusly charges 3-5% transaction fees on all reward redemptions, reducing the actual value employees receive. 
                        On a $10,000 annual reward budget, this equals $300-500 less value for your team.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* G2 Rankings Section */}
            <section id="ease-of-use" className="py-20 bg-muted/30 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="text-left mb-12"
              >
                <Badge variant="secondary" className="mb-4 px-4 py-2">
                  <Award className="mr-2 h-4 w-4" />
                  G2 Recognition Rankings
                </Badge>
                 <h2 className="text-4xl font-bold mb-4">
                   4. Ease of Use: Assembly wins in Highest Rated and Easiest to Use
                 </h2>
                <p className="text-base text-muted-foreground text-left mx-auto mb-2">
                  When comparing employee recognition software platforms, G2's independent user rankings reveal Assembly's superior user experience and implementation success. Real companies consistently rate Assembly #1 for both highest quality and easiest to use, making it the clear choice for organizations seeking seamless adoption and maximum user engagement. Unlike competitors, Assembly's intuitive design ensures quick deployment and high employee participation rates from day one.
                </p>
              </motion.div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-7xl mx-auto">
                {/* Highest Rated Rankings */}
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                >
                  <Card className="overflow-hidden shadow-xl border-0 h-full">
                    <div className="bg-gradient-to-r from-primary/10 to-primary/5 p-6">
                      <h3 className="text-2xl font-bold text-center mb-2 flex items-center justify-center gap-2">
                        <Award className="h-6 w-6 text-primary" />
                        Highest Rated
                      </h3>
                      <p className="text-center text-muted-foreground text-sm">
                        <a 
                          href="https://www.g2.com/categories/employee-recognition?tab=highest_rated" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          View on G2 →
                        </a>
                      </p>
                    </div>
                    <div className="p-6">
                      <div className="space-y-3">
                        {/* Assembly - #1 */}
                        <div className="relative flex items-center justify-between p-5 bg-gradient-to-r from-primary/15 to-primary/10 rounded-xl border-2 border-primary/30 shadow-lg shadow-primary/10">
                          <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-transparent rounded-xl"></div>
                          <div className="relative flex items-center space-x-3">
                            <div className="w-7 h-7 bg-primary/30 rounded-full flex items-center justify-center shadow-md">
                              <span className="text-xs font-bold text-primary">#1</span>
                            </div>
                            <div className="w-12 h-12 bg-gradient-to-br from-primary/20 to-primary/10 rounded-xl flex items-center justify-center shadow-sm">
                              <img 
                                src="/assets/assembly-logo-small.png" 
                                alt="Assembly Logo"
                                className="w-8 h-8 object-contain"
                              />
                            </div>
                            <div>
                              <div className="font-bold text-primary">Assembly</div>
                              <p className="text-xs text-muted-foreground">Employee Recognition</p>
                            </div>
                          </div>
                          <div className="relative flex items-center space-x-3">
                            <div className="text-right">
                              <div className="text-2xl font-bold text-primary">99</div>
                            </div>
                          </div>
                        </div>

                        {/* Bonusly - #4 */}
                        <div className="flex items-center justify-between p-3 bg-muted/10 rounded-lg border border-muted/30">
                          <div className="flex items-center space-x-3">
                            <div className="w-6 h-6 bg-muted/30 rounded-full flex items-center justify-center">
                              <span className="text-xs font-medium text-muted-foreground">#4</span>
                            </div>
                            <div className="w-10 h-10 bg-muted/20 rounded-lg flex items-center justify-center">
                              <img 
                                src="/assets/bonusly-logo-small.png" 
                                alt="Bonusly Logo"
                                className="w-6 h-6 object-contain"
                              />
                            </div>
                            <div>
                              <div className="font-medium text-muted-foreground">Bonusly</div>
                              <p className="text-xs text-muted-foreground/70">Recognition & Rewards</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-3">
                            <div className="text-right">
                              <div className="text-lg font-medium text-muted-foreground">98</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>
                </motion.div>

                {/* Easiest to Use Rankings */}
                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <Card className="overflow-hidden shadow-xl border-0 h-full">
                    <div className="bg-gradient-to-r from-primary/10 to-primary/5 p-6">
                      <h3 className="text-2xl font-bold text-center mb-2 flex items-center justify-center gap-2">
                        <Zap className="h-6 w-6 text-primary" />
                        Easiest to Use
                      </h3>
                      <p className="text-center text-muted-foreground text-sm">
                        <a 
                          href="https://www.g2.com/categories/employee-recognition?tab=easiest_to_use" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          View on G2 →
                        </a>
                      </p>
                    </div>
                    <div className="p-6">
                      <div className="space-y-3">
                        {/* Assembly - #1 */}
                        <div className="relative flex items-center justify-between p-5 bg-gradient-to-r from-primary/15 to-primary/10 rounded-xl border-2 border-primary/30 shadow-lg shadow-primary/10">
                          <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-transparent rounded-xl"></div>
                          <div className="relative flex items-center space-x-3">
                            <div className="w-7 h-7 bg-primary/30 rounded-full flex items-center justify-center shadow-md">
                              <span className="text-xs font-bold text-primary">#1</span>
                            </div>
                            <div className="w-12 h-12 bg-gradient-to-br from-primary/20 to-primary/10 rounded-xl flex items-center justify-center shadow-sm">
                              <img 
                                src="/assets/assembly-logo-small.png" 
                                alt="Assembly Logo"
                                className="w-8 h-8 object-contain"
                              />
                            </div>
                            <div>
                              <div className="font-bold text-primary">Assembly</div>
                              <p className="text-xs text-muted-foreground">Employee Recognition</p>
                            </div>
                          </div>
                          <div className="relative flex items-center space-x-3">
                            <div className="text-right">
                              <div className="text-2xl font-bold text-primary">9.5</div>
                            </div>
                          </div>
                        </div>

                        {/* Bonusly - #8 */}
                        <div className="flex items-center justify-between p-3 bg-muted/10 rounded-lg border border-muted/30">
                          <div className="flex items-center space-x-3">
                            <div className="w-6 h-6 bg-muted/30 rounded-full flex items-center justify-center">
                              <span className="text-xs font-medium text-muted-foreground">#8</span>
                            </div>
                            <div className="w-10 h-10 bg-muted/20 rounded-lg flex items-center justify-center">
                              <img 
                                src="/assets/bonusly-logo-small.png" 
                                alt="Bonusly Logo"
                                className="w-6 h-6 object-contain"
                              />
                            </div>
                            <div>
                              <div className="font-medium text-muted-foreground">Bonusly</div>
                              <p className="text-xs text-muted-foreground/70">Recognition & Rewards</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-3">
                            <div className="text-right">
                              <div className="text-lg font-medium text-muted-foreground">9.2</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              </div>
            </section>

            {/* Real User Reviews Highlights */}
            <section id="reviews" className="py-20">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="text-left mb-16"
              >
                <h2 className="text-4xl font-bold mb-4">5. Real User Reviews (Backed by G2 Data)</h2>
                <p className="text-base text-muted-foreground text-left mx-auto">
                  What actual users are saying about both platforms on <a href="https://www.g2.com/products/assembly-2021-12-09/reviews" target="_blank" rel="nofollow noopener" className="text-foreground/70 hover:text-foreground hover:underline">Assembly reviews on G2</a> and <a href="https://www.g2.com/products/bonusly-bonusly/reviews" target="_blank" rel="nofollow noopener" className="text-foreground/70 hover:text-foreground hover:underline">Bonusly reviews on G2</a> - the good and the areas for improvement
                </p>
              </motion.div>

              {/* Assembly Highlights */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="mb-16"
              >
                <Card className="overflow-hidden border-2 border-primary/20 shadow-xl">
                  <div className="bg-gradient-to-r from-primary/5 to-primary/10 p-8">
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center">
                        <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center mr-6">
                          <img 
                            src="/assets/assembly-logo-small.png" 
                            alt="Assembly Logo"
                            className="w-10 h-10 object-contain"
                          />
                        </div>
                        <div>
                        <h3 className="text-3xl font-bold">Assembly Highlights</h3>
                          <p className="text-muted-foreground mt-2">What users love about Assembly</p>
                        </div>
                      </div>
                      <Badge className="bg-primary/10 text-primary border-primary/20">4.9/5 Stars</Badge>
                    </div>
                  </div>

                  <div className="p-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                      <div>
                        <div className="text-2xl font-semibold mb-6 text-green-600 flex items-center">
                          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
                            <Check className="h-5 w-5 text-green-600" />
                          </div>
                          Top User Highlights
                        </div>
                        <div className="space-y-6 text-muted-foreground">
                          <div className="border-l-4 border-green-500 pl-4">
                            <div className="font-semibold text-foreground mb-2">Engaging & Positive Environment</div>
                            <p>Users consistently highlight how Assembly creates a genuinely uplifting workplace culture where recognition feels natural and meaningful.</p>
                          </div>
                          <div className="border-l-4 border-green-500 pl-4">
                            <div className="font-semibold text-foreground mb-2">Time-Saving AI Features</div>
                            <p>Managers report 75% time savings with Assembly's intelligent recognition system, addressing the common challenge of recognition being too time-consuming.</p>
                          </div>
                          <div className="border-l-4 border-green-500 pl-4">
                            <div className="font-semibold text-foreground mb-2">No Hidden Fees</div>
                            <p>Unlike Bonusly's transaction fees, Assembly ensures 100% of reward budgets reach employees, providing transparent value.</p>
                          </div>
                        </div>
                      </div>

                      <div>
                        <div className="text-2xl font-semibold mb-6 text-orange-600 flex items-center">
                          <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center mr-3">
                            <Clock className="h-5 w-5 text-orange-600" />
                          </div>
                          Areas for Growth
                        </div>
                        <div className="space-y-6 text-muted-foreground">
                          <div className="border-l-4 border-orange-500 pl-4">
                            <div className="font-semibold text-foreground mb-2">Analytics Depth</div>
                            <p>Some users would appreciate even more detailed analytics and reporting capabilities for deeper insights.</p>
                          </div>
                          <div className="border-l-4 border-orange-500 pl-4">
                            <div className="font-semibold text-foreground mb-2">Integration Breadth</div>
                            <p>While Assembly covers major platforms well, some users would welcome additional third-party integrations for niche tools.</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              </motion.div>

              {/* Bonusly Highlights */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <Card className="overflow-hidden shadow-xl">
                  <div className="bg-gradient-to-r from-muted/50 to-muted/30 p-8">
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center">
                        <div className="w-16 h-16 bg-secondary/10 rounded-xl flex items-center justify-center mr-6">
                          <img 
                            src="/assets/bonusly-logo-small.png" 
                            alt="Bonusly Logo"
                            className="w-10 h-10 object-contain"
                          />
                        </div>
                        <div>
                        <h3 className="text-3xl font-bold">Bonusly Highlights</h3>
                          <p className="text-muted-foreground mt-2">What users appreciate about Bonusly</p>
                        </div>
                      </div>
                      <Badge variant="outline">4.7/5 Stars</Badge>
                    </div>
                  </div>

                  <div className="p-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                      <div>
                        <div className="text-2xl font-semibold mb-6 text-green-600 flex items-center">
                          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
                            <Check className="h-5 w-5 text-green-600" />
                          </div>
                          User Positives
                        </div>
                        <div className="space-y-6 text-muted-foreground">
                          <div className="border-l-4 border-green-500 pl-4">
                            <div className="font-semibold text-foreground mb-2">Intuitive Interface</div>
                            <p>Users praise Bonusly's straightforward interface that makes basic peer recognition easy and accessible for most team members.</p>
                          </div>
                          <div className="border-l-4 border-green-500 pl-4">
                            <div className="font-semibold text-foreground mb-2">Workplace Integrations</div>
                            <p>Good integration with Slack and Teams allows recognition to flow within existing communication channels.</p>
                          </div>
                        </div>
                      </div>

                      <div>
                        <div className="text-2xl font-semibold mb-6 text-red-600 flex items-center">
                          <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center mr-3">
                            <X className="h-5 w-5 text-red-600" />
                          </div>
                          Common Complaints
                        </div>
                        <div className="space-y-6 text-muted-foreground">
                          <div className="border-l-4 border-red-500 pl-4">
                            <div className="font-semibold text-foreground mb-2">Poor Global Rewards</div>
                            <p>Multiple G2 reviews highlight limited international reward options, creating inequity for global teams.</p>
                          </div>
                          <div className="border-l-4 border-red-500 pl-4">
                            <div className="font-semibold text-foreground mb-2">Unresponsive Support</div>
                            <p>Users frequently mention customer support issues and generic responses that don't address specific problems.</p>
                          </div>
                          <div className="border-l-4 border-red-500 pl-4">
                            <div className="font-semibold text-foreground mb-2">Hidden Transaction Fees</div>
                            <p>Users complain about 3-5% PayPal processing fees that reduce the actual value of employee rewards.</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              </motion.div>
            </section>

            {/* ROI & Business Impact */}
            <section id="roi" className="py-20 bg-muted/30 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="text-left mb-16"
              >
                <h2 className="text-4xl font-bold mb-4">6. ROI & Business Impact</h2>
              </motion.div>

              <div className="max-w-6xl mx-auto">
                {/* Assembly Section */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className="mb-12"
                >
                  <Card className="overflow-hidden border-2 border-primary/20 shadow-xl">
                    <div className="bg-gradient-to-r from-primary/5 to-primary/10 p-8">
                      <div className="flex items-center">
                        <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center mr-6">
                          <img 
                            src="/assets/assembly-logo-small.png" 
                            alt="Assembly Logo"
                            className="w-10 h-10 object-contain"
                          />
                        </div>
                        <h3 className="text-3xl font-bold">Assembly</h3>
                      </div>
                    </div>

                    <div className="p-8">
                    <div className="space-y-6">
                      <div className="flex items-start">
                        <Check className="h-5 w-5 text-green-600 mr-4 mt-1 flex-shrink-0" />
                        <p className="text-muted-foreground leading-relaxed">
                          Tracks <a href="https://www.quantumworkplace.com/future-of-work/importance-of-employee-recognition" target="_blank" rel="nofollow noopener" className="text-foreground/70 hover:text-foreground hover:underline">recognition tied to business outcomes</a> alongside engagement scores, survey results, and retention metrics.
                        </p>
                      </div>
                      <div className="flex items-start">
                        <Check className="h-5 w-5 text-green-600 mr-4 mt-1 flex-shrink-0" />
                        <p className="text-muted-foreground leading-relaxed">
                          AI-driven insights connect recognition programs directly to business outcomes using the <a href="https://hbr.org/2011/05/the-power-of-small-wins" target="_blank" rel="nofollow noopener" className="text-foreground/70 hover:text-foreground hover:underline">progress principle</a>.
                        </p>
                      </div>
                      <div className="flex items-start">
                        <Check className="h-5 w-5 text-green-600 mr-4 mt-1 flex-shrink-0" />
                        <p className="text-muted-foreground leading-relaxed">
                          Helps HR and managers prove ROI with dashboards that <a href="https://www.gallup.com/analytics/472658/workplace-recognition-research.aspx" target="_blank" rel="nofollow noopener" className="text-foreground/70 hover:text-foreground hover:underline">reduce turnover risk</a> and strengthen culture.
                        </p>
                      </div>
                        <div className="flex items-start">
                          <Check className="h-5 w-5 text-green-600 mr-4 mt-1 flex-shrink-0" />
                          <p className="text-muted-foreground leading-relaxed">
                            Fast onboarding and high adoption rates drive quicker time-to-value.
                          </p>
                        </div>
                      </div>
                    </div>
                  </Card>
                </motion.div>

                {/* Bonusly Section */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="mb-12"
                >
                  <Card className="overflow-hidden shadow-xl">
                    <div className="bg-gradient-to-r from-muted/50 to-muted/30 p-8">
                      <div className="flex items-center">
                        <div className="w-16 h-16 bg-secondary/10 rounded-xl flex items-center justify-center mr-6">
                          <img 
                            src="/assets/bonusly-logo-small.png" 
                            alt="Bonusly Logo"
                            className="w-10 h-10 object-contain"
                          />
                        </div>
                        <h3 className="text-3xl font-bold">Bonusly</h3>
                      </div>
                    </div>

                    <div className="p-8">
                      <div className="space-y-6">
                        <div className="flex items-start">
                          <div className="w-5 h-5 rounded-full border-2 border-muted-foreground/30 mr-4 mt-1 flex-shrink-0"></div>
                          <p className="text-muted-foreground leading-relaxed">
                            Provides basic reporting on recognition activity and participation.
                          </p>
                        </div>
                        <div className="flex items-start">
                          <div className="w-5 h-5 rounded-full border-2 border-muted-foreground/30 mr-4 mt-1 flex-shrink-0"></div>
                          <p className="text-muted-foreground leading-relaxed">
                            Offers visibility into recognition networks (hashtags, peer interactions).
                          </p>
                        </div>
                        <div className="flex items-start">
                          <div className="w-5 h-5 rounded-full border-2 border-muted-foreground/30 mr-4 mt-1 flex-shrink-0"></div>
                          <p className="text-muted-foreground leading-relaxed">
                            ROI measurement is indirect and focused more on usage data than business outcomes.
                          </p>
                        </div>
                      </div>
                    </div>
                  </Card>
                </motion.div>

                {/* Winner Section */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <Card className="p-8 bg-gradient-to-r from-primary/5 to-secondary/5 border-primary/20">
                    <div className="flex items-center justify-start mb-4">
                      <Check className="h-6 w-6 text-green-600 mr-3" />
                      <div className="text-2xl font-bold text-primary">Winner: Assembly</div>
                    </div>
                    <p className="text-base text-muted-foreground leading-relaxed text-left">
                      <a href="https://www.gallup.com/workplace/650174/employee-retention-depends-getting-recognition-right.aspx" target="_blank" rel="nofollow noopener" className="text-foreground/70 hover:text-foreground hover:underline">Recognition reduces turnover</a> when tied directly to measurable outcomes like engagement, retention, and ROI, giving leaders clearer proof of impact.
                    </p>
                  </Card>
                </motion.div>
              </div>
            </section>

            {/* Which Platform Is Right for You */}
            <section id="comparison" className="py-20">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="text-left mb-16"
              >
                <h2 className="text-4xl font-bold mb-4">7. Which Platform Is Right for You?</h2>
              </motion.div>

              <div className="max-w-6xl mx-auto space-y-8">
                {/* Bonusly Section */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                >
                  <Card className="p-8 border border-muted/30">
                    <div className="flex items-center mb-6">
                      <div className="w-16 h-16 bg-secondary/10 rounded-xl flex items-center justify-center mr-6">
                        <img 
                          src="/assets/bonusly-logo-small.png" 
                          alt="Bonusly Logo"
                          className="w-10 h-10 object-contain"
                        />
                      </div>
                      <h3 className="text-2xl font-bold">Bonusly</h3>
                    </div>
                    <p className="text-base text-muted-foreground leading-relaxed">
                      <strong>Bonusly</strong> is a good fit if your organization wants a straightforward, peer-to-peer recognition tool with a large gift card catalog and doesn't need broader engagement features. It's simple, familiar, and works well for smaller teams.
                    </p>
                  </Card>
                </motion.div>

                {/* Assembly Section */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <Card className="p-8 border-2 border-primary/20 shadow-xl">
                    <div className="flex items-center mb-6">
                      <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center mr-6">
                        <img 
                          src="/assets/assembly-logo-small.png" 
                          alt="Assembly Logo"
                          className="w-10 h-10 object-contain"
                        />
                      </div>
                      <h3 className="text-2xl font-bold">Assembly</h3>
                    </div>
                    <p className="text-base text-muted-foreground leading-relaxed">
                      <strong>Assembly</strong>, on the other hand, is built for companies that want more than recognition alone. With AI-powered recognition, <a href="https://www.shrm.org/topics-tools/news/benefits-compensation/how-flexible-rewards-programs-boost-employee-engagement" target="_blank" rel="nofollow noopener" className="text-foreground/70 hover:text-foreground hover:underline">flexible rewards</a>, advanced analytics, and added tools like surveys, announcements, and manager 1:1s, Assembly delivers greater long-term value.
                    </p>
                  </Card>
                </motion.div>

                {/* Recommendation */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  <Card className="p-8 bg-gradient-to-r from-primary/5 to-secondary/5 border-primary/20">
                    <div className="text-left flex items-center gap-4">
                      <div className="text-3xl">👉</div>
                      <p className="text-base leading-relaxed">
                        If you need a recognition platform that grows with your culture and ties directly to business outcomes, <strong className="text-primary">Assembly is the stronger choice</strong>.
                      </p>
                    </div>
                  </Card>
                </motion.div>
              </div>
            </section>

            {/* Conclusion */}
            <section id="conclusion" className="py-20 bg-muted/30 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="max-w-6xl mx-auto text-left"
              >
                <h2 className="text-4xl font-bold mb-8">8. Conclusion</h2>
                <div className="prose prose-lg mx-auto text-left">
                  <p className="text-base text-muted-foreground leading-relaxed mb-8">
                    Both <strong>Assembly and Bonusly</strong> can help teams recognize contributions and boost morale. Bonusly offers simple peer-to-peer recognition, but Assembly goes further — combining recognition with rewards, AI-powered tools, and engagement features that deliver measurable ROI.
                  </p>
                </div>
                
                <Card className="p-8 border-primary/20">
                  <h3 className="text-2xl font-bold mb-4 text-center">Ready to see the difference?</h3>
                  <p className="text-base text-muted-foreground mb-6 text-center">
                    🎉 <strong>Start free with Assembly today</strong> and give your team a modern recognition experience that grows with your culture.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button size="lg" className="px-8 py-4 text-lg font-semibold" onClick={() => setIsSupademoModalOpen(true)}>
                      <Play className="mr-2 h-5 w-5" />
                      See Assembly in Action
                    </Button>
                    <Button variant="outline" size="lg" className="px-8 py-4 text-lg font-semibold" asChild>
                      <a href="/pricing">
                        <ArrowRight className="mr-2 h-5 w-5" />
                        Start Free
                      </a>
                    </Button>
                  </div>
                </Card>
              </motion.div>
            </section>

            {/* FAQs */}
            <section id="faqs" className="py-20">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="text-left mb-16"
              >
                <h2 className="text-4xl font-bold mb-4">9. Frequently Asked Questions</h2>
                <p className="text-base text-muted-foreground text-left mx-auto">
                  Common questions about choosing between the platforms
                </p>
              </motion.div>

              <div className="max-w-6xl mx-auto space-y-6">
                {[
                  {
                    question: "Is Assembly better than Bonusly for small businesses?",
                    answer: "Yes, Assembly is particularly advantageous for small businesses because it offers a free forever plan (up to 50 users) while Bonusly has no free option. Additionally, Assembly's no transaction fee policy means small businesses keep 100% of their recognition budget, whereas Bonusly deducts 3-5% in fees. Small businesses also benefit from Assembly's 24/7 support and faster deployment."
                  },
                  {
                    question: "Which tool has better ROI according to users?",
                    answer: "Assembly delivers superior ROI based on user data and business outcomes. Users report 31% lower turnover, 75% time savings for managers, and 12× better likelihood of achieving business goals. Assembly's transparent pricing with no hidden fees, combined with its comprehensive feature set, provides better value than Bonusly's limited platform with transaction costs."
                  },
                  {
                    question: "How do the G2 ratings compare between Assembly and Bonusly?",
                    answer: "Assembly significantly outperforms Bonusly on G2: Assembly ranks #1 in both 'Highest Rated' (99/100) and 'Easiest to Use' (9.5/10) categories, while Bonusly ranks #4 in Highest Rated (98/100) and #8 in Easiest to Use (9.2/10). Assembly also has a higher overall rating (4.9/5 vs 4.7/5)."
                  },
                  {
                    question: "What are the main differences in pricing models?",
                    answer: "Assembly offers transparent pricing starting at $3/user/month with a free forever plan, no transaction fees, and points that never expire. Bonusly starts at $5/user/month with no free plan, charges 3-5% transaction fees on rewards, and has point expiration policies. Assembly's model ensures 100% of your recognition budget reaches employees."
                  },
                  {
                    question: "Which platform is better for global teams?",
                    answer: "Assembly is superior for global teams. Multiple G2 reviews highlight Bonusly's poor international reward selection, leaving global employees with limited meaningful options. Assembly provides equal access to quality rewards worldwide, ensuring equity across all team members regardless of location."
                  },
                  {
                    question: "How do the AI and automation features compare?",
                    answer: "Assembly offers AI-powered recognition that saves managers 75% of their time, intelligent insights, and automated workflows. Bonusly lacks AI features entirely, requiring manual processes for recognition. This makes Assembly significantly more efficient for scaling recognition programs as teams grow."
                  },
                  {
                    question: "What do users say about customer support?",
                    answer: "Assembly provides 24/7 customer support and unlimited training, which users consistently praise. Bonusly users frequently complain about unresponsive customer support and generic responses that don't address specific issues. This difference becomes critical when you need help with platform issues or optimization."
                  },
                  {
                    question: "Can I migrate from Bonusly to Assembly?",
                    answer: "Yes, Assembly provides migration support to help you transition from Bonusly. The process includes data export assistance, setup guidance, and training for your team. Many companies make this switch to eliminate transaction fees, gain AI-powered features, and access superior customer support."
                  }
                ].map((faq, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: index * 0.05 }}
                  >
                    <Card className="p-6">
                      <div className="font-semibold mb-3">{faq.question}</div>
                      <p className="text-muted-foreground leading-relaxed">{faq.answer}</p>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </section>

          </div>
        </main>

        {/* Mobile Sticky CTA */}
        <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-background/95 backdrop-blur-sm border-t border-border p-4 z-30">
          <Button 
            className="w-full" 
            size="lg"
            onClick={() => setIsSupademoModalOpen(true)}
          >
            <ArrowRight className="mr-2 h-5 w-5" />
            See Assembly Pricing
          </Button>
        </div>
      </div>
      
      <DemoBookingModal open={isDemoModalOpen} onOpenChange={setIsDemoModalOpen} />
      <SupademoModal open={isSupademoModalOpen} onOpenChange={setIsSupademoModalOpen} />
    </CompanyLayout>
  );
};

export default AssemblyVsBonusly;